"use client";

import { useRef, useState } from "react";
import { toast } from "sonner";
import { toastApiError } from "@/lib/api-toast";
import { cancelAuction, deleteAuction } from "../../api";
import type { AuctionItem } from "../../types";
export type OwnedAction = "cancel" | "delete";

/** AuctionListActions' rules: edit and cancel a published draft; delete anything published. */
export function ownedPermissions(a: AuctionItem) {
  return {
    canEdit: a.status === "draft" && a.is_published,
    canCancel: a.status === "draft" && a.is_published,
    canDelete: a.is_published
  };
}

/**
 * Cancel / delete from the owner's list — AuctionListActions' flow, moved
 * here unchanged and lifted to the list: one confirm dialog for whichever
 * row asked. On success the list is refreshed; an error surfaces through the
 * app's axios toast and the dialog stays open, as before.
 */
export function useOwnedAuctionActions({
  lang,
  onChanged
}: {
  lang: "fa" | "en";
  onChanged: () => void;
}) {
  const isFa = lang === "fa";
  const [pending, setPending] = useState<{
    auction: AuctionItem;
    action: OwnedAction;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // One request at a time; the dialog is held open while it is out.
  const inFlight = useRef(false);
  const confirm = async () => {
    if (!pending || inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);
    try {
      if (pending.action === "delete") {
        await deleteAuction(pending.auction.id);
        toast.success(isFa ? "مزایده حذف شد" : "Auction deleted");
      } else {
        await cancelAuction(pending.auction.id);
        toast.success(isFa ? "مزایده لغو شد" : "Auction canceled");
      }
      setPending(null);
      onChanged();
    } catch (error) {
      // The axios toast has said why; the catch only keeps the failure from escaping the click.
      toastApiError(error, isFa ? "انجام نشد؛ دوباره امتحان کنید." : "That didn't go through — try again.");
    } finally {
      inFlight.current = false;
      setIsSubmitting(false);
    }
  };
  return {
    pending,
    ask: (auction: AuctionItem, action: OwnedAction) => setPending({
      auction,
      action
    }),
    close: () => {
      if (!inFlight.current) setPending(null);
    },
    confirm,
    isSubmitting
  };
}